/**
 * TimelineRail — shared aspect. Vertical milestone list with a thin
 * hairline connector running through circular status markers. Each
 * row has a marker, a label, an optional inline note, and a
 * right-aligned date. Used by /brand Aspect 18 AND by the real
 * Student program dashboard (DashboardTimelineList).
 *
 * Status semantics (resolved internally from PaneTheme + COLORS):
 *   complete  — filled forest marker with offw checkmark
 *   active    — filled amber marker with amber halo glow
 *   upcoming  — white marker with dashed sand-line border
 *
 * Theme tokens consumed: surfaceRaisedBg, border, accent, success,
 * textOnSurface, textOnSurfaceDim, textOnSurfaceFaint, accentDeep,
 * accentHalo, displayMediumFont, bodyMediumFont, bodySemiBoldFont.
 * COLORS consumed: FOREST, AMBER, OFFW, SAGE_DEEP, SAND_LINE.
 */
import { Platform, Text, View, type ViewStyle } from "react-native";
import Svg, { Path } from "react-native-svg";

import { COLORS, RADII, SPACING, STROKES } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";

export type TimelineRailStatus = "complete" | "active" | "upcoming";

export type TimelineRailItem = {
  key: string;
  label: string;
  dateLabel: string;
  status: TimelineRailStatus;
  note?: string;
};

export type TimelineRailProps = {
  theme: PaneTheme;
  items: TimelineRailItem[];
};

export function TimelineRail({ theme, items }: TimelineRailProps) {
  return (
    <View
      style={[
        {
          backgroundColor: theme.surfaceRaisedBg,
          borderRadius: RADII.md,
          borderWidth: STROKES.hairline,
          borderColor: theme.border,
          paddingVertical: SPACING.md,
          paddingHorizontal: 22,
        },
        Platform.OS === "web"
          ? ({
              boxShadow:
                "0 1px 2px rgba(28,43,26,0.04), 0 4px 14px rgba(28,43,26,0.05)",
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      <View style={{ position: "relative" }}>
        {/* Hairline connector behind the markers. Markers are 18px wide,
            so the line sits 9px from the left edge of each row. */}
        <View
          pointerEvents="none"
          style={{
            position: "absolute",
            left: 9,
            top: 9,
            bottom: 9,
            width: 1,
            backgroundColor: COLORS.SAND_LINE.hex,
          }}
        />
        {items.map((item, i) => (
          <TimelineRow
            key={item.key}
            item={item}
            isLast={i === items.length - 1}
            theme={theme}
          />
        ))}
      </View>
    </View>
  );
}

function TimelineRow({
  item,
  isLast,
  theme,
}: {
  item: TimelineRailItem;
  isLast: boolean;
  theme: PaneTheme;
}) {
  const noteColor =
    item.status === "active"
      ? theme.accentDeep
      : item.status === "complete"
      ? COLORS.SAGE_DEEP.hex
      : theme.textOnSurfaceFaint;
  const labelColor =
    item.status === "upcoming" ? theme.textOnSurfaceDim : theme.textOnSurface;
  const dateColor =
    item.status === "active" ? theme.accentDeep : theme.textOnSurfaceDim;
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 14,
        paddingBottom: isLast ? 0 : 11,
      }}
    >
      <TimelineMarker status={item.status} theme={theme} />
      <View
        style={{
          flex: 1,
          flexDirection: "row",
          alignItems: "baseline",
          gap: 12,
        }}
      >
        <View
          style={{
            flex: 1,
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "baseline",
          }}
        >
          <Text
            style={{
              fontFamily: theme.displayMediumFont,
              fontSize: 14.5,
              color: labelColor,
            }}
            numberOfLines={1}
          >
            {item.label}
          </Text>
          {item.note ? (
            <Text
              style={{
                marginLeft: 8,
                fontFamily: theme.bodyMediumFont,
                fontSize: 11.5,
                color: noteColor,
              }}
            >
              {`· ${item.note}`}
            </Text>
          ) : null}
        </View>
        <Text
          style={{
            fontFamily: theme.bodySemiBoldFont,
            fontSize: 12,
            color: dateColor,
            fontVariant: ["tabular-nums"],
          }}
        >
          {item.dateLabel}
        </Text>
      </View>
    </View>
  );
}

function TimelineMarker({
  status,
  theme,
}: {
  status: TimelineRailStatus;
  theme: PaneTheme;
}) {
  const isWeb = Platform.OS === "web";
  const size = 18;
  const base = {
    width: size,
    height: size,
    borderRadius: size / 2,
    alignItems: "center" as const,
    justifyContent: "center" as const,
  };

  if (status === "complete") {
    return (
      <View
        style={{
          ...base,
          backgroundColor: COLORS.FOREST.hex,
          borderWidth: STROKES.hairline,
          borderColor: COLORS.FOREST.hex,
        }}
      >
        <Svg width={9} height={9} viewBox="0 0 12 12" fill="none">
          <Path
            d="M2.5 6.2 L4.7 8.4 L9.2 3.8"
            stroke={COLORS.OFFW.hex}
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      </View>
    );
  }
  if (status === "active") {
    return (
      <View
        style={[
          {
            ...base,
            backgroundColor: COLORS.AMBER.hex,
            borderWidth: STROKES.hairline,
            borderColor: COLORS.AMBER.hex,
          },
          isWeb
            ? ({ boxShadow: `0 0 0 3px ${theme.accentHalo}` } as unknown as ViewStyle)
            : null,
        ]}
      />
    );
  }
  // upcoming
  return (
    <View
      style={{
        ...base,
        backgroundColor: theme.surfaceRaisedBg,
        borderWidth: STROKES.border,
        borderColor: COLORS.SAND_LINE.hex,
        borderStyle: "dashed",
      }}
    />
  );
}
